import { fetchJson } from "@/lib/api";

export type SerializedSessionRow = {
  sessionId: string | null;
  userId: string | null;
  status: string | null;
  startedAt: string | null;
  endedAt: string | null;
  endReason: string | null;
  postSurveyStatus: string | null;
  lastReadingAt: string | null;
  timezoneName?: string | null;
  timezoneOffset?: number | null;
};

export type CurrentParticipantSessionPhase =
  | "post_survey_pending"
  | "active"
  | null;

export type CurrentParticipantSessionResponse = {
  session: SerializedSessionRow | null;
  phase: CurrentParticipantSessionPhase;
  requiresPostSurvey: boolean;
};

export type PendingPostSurveyRecoveryRecord = {
  userId: string;
  sessionId: string;
  updatedAt: string;
};

export const PENDING_POST_SURVEY_STORAGE_KEY = "imags:pending-post-survey";
const LOGOUT_AFTER_POST_SURVEY_QUERY_VALUE = "1";

export function normalizeSessionId(value: string | null | undefined) {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  return trimmed ? trimmed : null;
}

function normalizeRecoveryRecord(
  value: unknown
): PendingPostSurveyRecoveryRecord | null {
  if (!value || typeof value !== "object") return null;

  const record = value as Record<string, unknown>;
  const userId =
    typeof record.userId === "string" ? record.userId.trim() : "";
  const sessionId =
    typeof record.sessionId === "string" ? record.sessionId.trim() : "";
  const updatedAt =
    typeof record.updatedAt === "string" ? record.updatedAt.trim() : "";

  if (!userId || !sessionId || !updatedAt) {
    return null;
  }

  return {
    userId,
    sessionId,
    updatedAt,
  };
}

export function buildPostSurveyPath(
  sessionId: string,
  options?: { logout?: boolean }
) {
  const params = new URLSearchParams({ sessionId });
  if (options?.logout) {
    params.set("logout", LOGOUT_AFTER_POST_SURVEY_QUERY_VALUE);
  }
  return `/postsurvey?${params.toString()}`;
}

export async function fetchCurrentParticipantSession() {
  return fetchJson<CurrentParticipantSessionResponse>("/api/sessions/current");
}

export function readPendingPostSurveyRecovery(
  expectedUserId?: string | null
): PendingPostSurveyRecoveryRecord | null {
  if (typeof window === "undefined") return null;

  try {
    const raw = window.localStorage.getItem(PENDING_POST_SURVEY_STORAGE_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw);
    const record = normalizeRecoveryRecord(parsed);
    if (!record) {
      window.localStorage.removeItem(PENDING_POST_SURVEY_STORAGE_KEY);
      return null;
    }
    if (expectedUserId && record.userId !== expectedUserId) {
      return null;
    }
    return record;
  } catch {
    try {
      window.localStorage.removeItem(PENDING_POST_SURVEY_STORAGE_KEY);
    } catch {
      // Ignore storage cleanup failures.
    }
    return null;
  }
}

export function writePendingPostSurveyRecovery(
  record: PendingPostSurveyRecoveryRecord
) {
  if (typeof window === "undefined") return;

  try {
    window.localStorage.setItem(
      PENDING_POST_SURVEY_STORAGE_KEY,
      JSON.stringify(record)
    );
  } catch {
    // Ignore storage write failures.
  }
}

export function refreshPendingPostSurveyRecovery(
  userId: string,
  sessionId: string
) {
  writePendingPostSurveyRecovery({
    userId,
    sessionId,
    updatedAt: new Date().toISOString(),
  });
}

export function clearPendingPostSurveyRecovery() {
  if (typeof window === "undefined") return;

  try {
    window.localStorage.removeItem(PENDING_POST_SURVEY_STORAGE_KEY);
  } catch {
    // Ignore storage cleanup failures.
  }
}
